import { useState, useEffect } from 'react';
import { Job, JobFilters, JobListResponse } from '@/types/job';
import { supabase } from '@/integrations/supabase/client';

const parseXMLJobs = (xmlText: string): Job[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
  
  // Check for parsing errors
  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Failed to parse XML');
  }
  
  const jobElements = xmlDoc.querySelectorAll('job, position, vacancy');
  const jobs: Job[] = [];
  
  // Helper function to validate email
  const isValidEmail = (email: string): boolean => {
    return email.includes('@') && email.includes('.') && email.length > 5;
  };
  
  jobElements.forEach((jobElement, index) => {
    const getTextContent = (selector: string): string => {
      const element = jobElement.querySelector(selector) || 
                    jobElement.querySelector(selector.toLowerCase()) ||
                    jobElement.querySelector(selector.toUpperCase());
      
      let content = element?.textContent?.trim() || '';
      
      // Handle CDATA sections - remove CDATA wrapper if present
      if (content.startsWith('<![CDATA[') && content.endsWith(']]>')) {
        content = content.slice(9, -3);
      }
      
      // Clean up any remaining HTML entities or tags
      content = content.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
      
      return content;
    };

    // Helper function to get content from nested contact block
    const getContactContent = (selector: string): string => {
      const contactBlock = jobElement.querySelector('contact');
      if (contactBlock) {
        const element = contactBlock.querySelector(selector);
        if (element) {
          let content = element.textContent?.trim() || '';
          // Handle CDATA sections
          if (content.startsWith('<![CDATA[') && content.endsWith(']]>')) {
            content = content.slice(9, -3);
          }
          // Additional cleaning for email fields - remove any remaining XML artifacts
          if (selector === 'email') {
            content = content.replace(/[<>]/g, '').replace(/&[a-zA-Z0-9]+;/g, '').trim();
          }
          return content;
        }
      }
      return '';
    };
    
    const jobId = getTextContent('id') || getTextContent('jobreferencenumber') || `job-${index}`;
    
    // Extract recruiter image from <recruiter> tag
    const recruiterImage = getTextContent('recruiter');
    
    // Extract contact information from nested <contact> block
    const contactFirstName = getContactContent('firstname');
    const contactLastName = getContactContent('lastname');
    const contactEmail = getContactContent('email');
    const contactPhone = getContactContent('phone');
    const contactPosition = getContactContent('position');
    const contactStrasse = getContactContent('strasse');
    const contactPlz = getContactContent('plz');
    const contactOrt = getContactContent('ort');
    const contactCompany = getContactContent('company');
    
    // Construct full contact person name
    let fullContactName = '';
    if (contactFirstName && contactLastName) {
      fullContactName = `${contactFirstName} ${contactLastName}`;
    }
    
    // Construct full address
    let fullAddress = '';
    if (contactStrasse && contactPlz && contactOrt) {
      fullAddress = `${contactStrasse}, ${contactPlz} ${contactOrt}`;
    }
    
    // Clean the email extracted from XML
    const cleanedContactEmail = contactEmail ? contactEmail.replace(/[<>]/g, '').replace(/&[a-zA-Z0-9]+;/g, '').trim() : '';
    
    console.log('Email processing debug:', {
      jobId,
      title: getTextContent('title'),
      rawContactEmail: contactEmail,
      cleanedContactEmail,
      isValidEmail: cleanedContactEmail ? isValidEmail(cleanedContactEmail) : false,
      fullContactName
    });
    
    // Map contact email based on contact name, since XML has inconsistent data
    let validContactEmail = '';
    if (fullContactName.toLowerCase().includes('yelva')) {
      validContactEmail = 'y.scholz@scholz-interim.de';
      console.log(`Using Yelva's email for ${jobId}: ${validContactEmail}`);
    } else if (fullContactName.toLowerCase().includes('michael')) {
      validContactEmail = 'm.scholz@scholz-interim.de';
      console.log(`Using Michael's email for ${jobId}: ${validContactEmail}`);
    } else {
      // If we have a valid email from XML and it doesn't conflict with known contacts, use it
      if (cleanedContactEmail && isValidEmail(cleanedContactEmail)) {
        validContactEmail = cleanedContactEmail;
        console.log(`Using XML email for ${jobId}: ${validContactEmail}`);
      } else {
        validContactEmail = 'm.scholz@scholz-interim.de'; // Default fallback
        console.log(`Using default email for ${jobId}: ${validContactEmail}`);
      }
    }
    
    // Map contact phone based on contact name
    let validContactPhone = contactPhone || getTextContent('phone') || getTextContent('contactphone');
    if (fullContactName.toLowerCase().includes('michael')) {
      validContactPhone = validContactPhone?.replace('4263117', '4263112') || '+49 4141 - 4263112';
      console.log(`Using Michael's phone for ${jobId}: ${validContactPhone}`);
    } else {
      validContactPhone = validContactPhone || '+49 4141 - 4263117';
      console.log(`Using default phone for ${jobId}: ${validContactPhone}`);
    }
    
    const job: Job = {
      id: jobId,
      title: getTextContent('title') || getTextContent('jobtitle') || getTextContent('position'),
      company: getTextContent('company') || getTextContent('employer') || getTextContent('companyname') || 'Scholz Interim',
      location: getTextContent('location') || getTextContent('city') || getTextContent('workplace') || getTextContent('region'),
      department: getTextContent('department') || getTextContent('category') || getTextContent('sector') || getTextContent('jobfield'),
      employmentType: getTextContent('employmenttype') || getTextContent('jobtype') || getTextContent('contract') || getTextContent('workingtime'),
      description: getTextContent('description') || getTextContent('jobdescription') || getTextContent('text') || getTextContent('summary'),
      requirements: getTextContent('requirements') || getTextContent('qualifications') || getTextContent('profile'),
      benefits: getTextContent('benefits') || getTextContent('perks') || getTextContent('offer'),
      salary: getTextContent('salary') || getTextContent('compensation') || getTextContent('wage'),
      publishDate: getTextContent('publishdate') || getTextContent('dateposted') || getTextContent('created') || getTextContent('date'),
      applicationDeadline: getTextContent('deadline') || getTextContent('applicationdeadline'),
      contactEmail: validContactEmail,
      applyUrl: getTextContent('applyurl') || getTextContent('applicationurl') || getTextContent('url') || getTextContent('link') || `https://k61286.coveto.de/public/jobs/apply/${jobId}`,
      imageUrl: getTextContent('imageurl') || getTextContent('image'),
      companyLogo: getTextContent('companylogo') || getTextContent('logo'),
      // Use parsed contact information with fallbacks
      contactPerson: fullContactName || getTextContent('contactperson') || getTextContent('contact-person') || getTextContent('recruiter') || 'Michael Scholz',
      contactTitle: contactPosition || getTextContent('contacttitle') || getTextContent('contact-title') || 'Geschäftsführer',
      contactPhone: validContactPhone,
      contactAddress: fullAddress || getTextContent('address') || getTextContent('contactaddress') || 'Römerstraße 6, 21680 Stade',
      contactCity: contactOrt || getTextContent('contactcity') || getTextContent('contact-city') || 'Stade',
      contactPostalCode: contactPlz || getTextContent('postalcode') || getTextContent('zipcode') || '21680',
      contactImage: recruiterImage || getTextContent('contactimage') || getTextContent('contact-image') || 'https://k61286.coveto.de/public_data/amaBenutzer/3/3.jpg'
    };
    
    // Only add jobs with at least a title
    if (job.title) {
      jobs.push(job);
    }
  });
  
  return jobs;
};

export const useJobs = (filters?: JobFilters): JobListResponse => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        setError(undefined);
        
        // Try to load jobs from database first
        console.log('Fetching jobs from database...');
        const { data: dbJobs, error: dbError } = await supabase
          .from('jobs')
          .select('*')
          .order('publish_date', { ascending: false });

        if (dbError) {
          console.error('Database error:', dbError);
          throw new Error('Fehler beim Laden der Stellenanzeigen aus der Datenbank');
        }

        if (dbJobs && dbJobs.length > 0) {
          // Convert database format to Job interface
          const convertedJobs: Job[] = dbJobs.map(dbJob => ({
            id: dbJob.id,
            title: dbJob.title,
            company: dbJob.company,
            location: dbJob.location,
            department: dbJob.department || undefined,
            employmentType: dbJob.employment_type || undefined,
            description: dbJob.description,
            requirements: dbJob.requirements || undefined,
            benefits: dbJob.benefits || undefined,
            salary: dbJob.salary || undefined,
            publishDate: dbJob.publish_date,
            applicationDeadline: dbJob.application_deadline || undefined,
            contactEmail: dbJob.contact_email || undefined,
            applyUrl: dbJob.apply_url || undefined,
            imageUrl: dbJob.image_url || undefined,
            companyLogo: dbJob.company_logo || undefined,
            contactPerson: dbJob.contact_person || undefined,
            contactTitle: dbJob.contact_title || undefined,
            contactPhone: dbJob.contact_phone || undefined,
            contactAddress: dbJob.contact_address || undefined,
            contactCity: dbJob.contact_city || undefined,
            contactPostalCode: dbJob.contact_postal_code || undefined,
            contactImage: dbJob.contact_image || undefined,
          }));

          console.log(`✓ Loaded ${convertedJobs.length} jobs from database`);
          setJobs(convertedJobs);
          setIsLoading(false);
          return;
        }

        // Fallback: Load from XML if database is empty
        console.log('Database empty, falling back to XML...');
        const targetUrl = 'https://k61286.coveto.de/public/xml';
        
        // Create fetch functions for each proxy with timeout
        const createProxyFetch = (proxy: string) => {
          return new Promise<string>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              reject(new Error(`Timeout for proxy: ${proxy}`));
            }, 8000);
            
            const fetchData = async () => {
              try {
                let response;
                
                if (proxy.includes('allorigins')) {
                  response = await fetch(`${proxy}${encodeURIComponent(targetUrl)}`);
                  if (response.ok) {
                    const data = await response.json();
                    clearTimeout(timeoutId);
                    console.log(`✓ Successfully loaded jobs via ${proxy.split('/')[2]}`);
                    resolve(data.contents);
                  } else {
                    throw new Error(`HTTP ${response.status}`);
                  }
                } else {
                  response = await fetch(`${proxy}${targetUrl}`);
                  if (response.ok) {
                    const text = await response.text();
                    clearTimeout(timeoutId);
                    console.log(`✓ Successfully loaded jobs via ${proxy.split('/')[2]}`);
                    resolve(text);
                  } else {
                    throw new Error(`HTTP ${response.status}`);
                  }
                }
              } catch (error) {
                clearTimeout(timeoutId);
                reject(error);
              }
            };
            
            fetchData();
          });
        };
        
        const proxyUrls = [
          'https://api.allorigins.win/get?url=',
          'https://corsproxy.io/?',
          'https://cors-anywhere.herokuapp.com/',
        ];
        
        const proxyPromises = proxyUrls.map(createProxyFetch);
        
        let xmlText: string;
        try {
          xmlText = await new Promise<string>((resolve, reject) => {
            let rejectedCount = 0;
            const errors: Error[] = [];
            
            proxyPromises.forEach((promise) => {
              promise
                .then((result) => {
                  resolve(result);
                })
                .catch((error) => {
                  errors.push(error);
                  rejectedCount++;
                  if (rejectedCount === proxyPromises.length) {
                    reject(new Error('Alle CORS-Proxies sind nicht verfügbar. Versuchen Sie es später erneut.'));
                  }
                });
            });
          });
          console.log('Successfully fetched XML data via parallel proxy requests');
        } catch (error) {
          console.error('All proxy requests failed:', error);
          throw new Error('Alle CORS-Proxies sind nicht verfügbar. Versuchen Sie es später erneut.');
        }
        
        const parsedJobs = parseXMLJobs(xmlText);
        console.log(`Successfully parsed ${parsedJobs.length} jobs from XML`);
        
        setJobs(parsedJobs);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError(err instanceof Error ? err.message : 'Fehler beim Laden der Stellenanzeigen');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchJobs();
  }, []);
  
  // Apply filters
  const filteredJobs = jobs.filter(job => {
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      if (!job.title.toLowerCase().includes(searchLower) && 
          !job.description.toLowerCase().includes(searchLower) &&
          !job.company.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    if (filters?.location && !job.location.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    
    if (filters?.department && job.department && !job.department.toLowerCase().includes(filters.department.toLowerCase())) {
      return false;
    }
    
    if (filters?.employmentType && job.employmentType && !job.employmentType.toLowerCase().includes(filters.employmentType.toLowerCase())) {
      return false;
    }
    
    return true;
  });
  
  // Apply sorting
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (!filters?.sortBy) return 0;
    
    const direction = filters.sortOrder === 'desc' ? -1 : 1;
    
    switch (filters.sortBy) {
      case 'title':
        return direction * a.title.localeCompare(b.title);
      case 'company':
        return direction * a.company.localeCompare(b.company);
      case 'location':
        return direction * a.location.localeCompare(b.location);
      case 'publishDate':
        return direction * new Date(a.publishDate).getTime() - new Date(b.publishDate).getTime();
      default:
        return 0;
    }
  });

  return {
    jobs: sortedJobs,
    allJobs: jobs,
    total: sortedJobs.length,
    isLoading,
    error
  };
};